import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ArrowLeft, Upload, Loader2, FileText, Image, Trash2, Check,
  AlertTriangle, CheckCircle, XCircle, Save, Send, X, ChevronDown, Search, Car
} from 'lucide-react'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

const CONFIDENCE_LEVELS = {
  high: { min: 0.8, color: 'text-emerald-600 bg-emerald-50 border-emerald-200', label: 'Fiable' },
  medium: { min: 0.5, color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'A vérifier' },
  low: { min: 0, color: 'text-red-500 bg-red-50 border-red-200', label: 'Non détecté' },
}

const getConfidence = (score) => {
  if (score >= 0.8) return CONFIDENCE_LEVELS.high
  if (score >= 0.5) return CONFIDENCE_LEVELS.medium
  return CONFIDENCE_LEVELS.low
}

const TYPE_SINISTRE_OPTIONS = [
  { value: 'rc_materiel', label: 'RC matériel' },
  { value: 'rc_corporel', label: 'RC corporel' },
  { value: 'dta', label: 'Dommages tous accidents' },
  { value: 'amiable', label: 'Amiable' },
  { value: 'vol', label: 'Vol' },
  { value: 'incendie', label: 'Incendie' },
  { value: 'bris_de_glace', label: 'Bris de glace' },
  { value: 'defense_recours', label: 'Défense et recours' },
  { value: 'protection_juridique', label: 'Protection juridique' },
  { value: 'dab', label: 'Dommages aux biens' },
  { value: 'autre', label: 'Autre' },
]

const RESPONSABILITE_OPTIONS = [
  { value: 'responsable', label: 'Responsable' },
  { value: 'non_responsable', label: 'Non responsable' },
  { value: 'partage_50_50', label: 'Partage 50/50' },
  { value: 'partage_autre', label: 'Partage autre' },
  { value: 'en_cours', label: 'En cours de détermination' },
  { value: 'sans_objet', label: 'Sans objet' },
]

export default function PreOuvertureDetail({ tenants }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const isNew = id === 'nouveau'
  const fileInputRef = useRef(null)

  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [analysing, setAnalysing] = useState(false)
  const [validating, setValidating] = useState(false)

  // Pre-ouverture data
  const [tenantId, setTenantId] = useState('')
  const [contenuMail, setContenuMail] = useState('')
  const [notesGestionnaire, setNotesGestionnaire] = useState('')
  const [statut, setStatut] = useState('brouillon')
  const [piecesJointes, setPiecesJointes] = useState([])
  const [sinistreId, setSinistreId] = useState(null)

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState({}) // { filename: { progress: 0-100, status: 'uploading'|'done'|'error' } }

  // AI extracted data (editable form)
  const [form, setForm] = useState({})
  const [rawExtracted, setRawExtracted] = useState({})

  // Vehicle search
  const [vehiculeSearch, setVehiculeSearch] = useState('')
  const [vehiculeResults, setVehiculeResults] = useState([])
  const [vehiculeSearching, setVehiculeSearching] = useState(false)
  const [showVehiculeDropdown, setShowVehiculeDropdown] = useState(false)
  const [selectedVehicule, setSelectedVehicule] = useState(null)
  const vehiculeSearchRef = useRef(null)
  const vehiculeDropdownRef = useRef(null)

  // Reference data
  const [vehicules, setVehicules] = useState([])
  const [chauffeurs, setChauffeurs] = useState([])
  const [polices, setPolices] = useState([])

  // Load pre-ouverture if editing
  useEffect(() => {
    if (!isNew) {
      (async () => {
        try {
          const r = await fetch(`${API}/api/pre-ouvertures/${id}`)
          const d = await r.json()
          const po = d.pre_ouverture
          setTenantId(po.tenant_id)
          setContenuMail(po.contenu_mail)
          setNotesGestionnaire(po.notes_gestionnaire || '')
          setStatut(po.statut)
          setPiecesJointes(po.pieces_jointes || [])
          setSinistreId(po.sinistre_id)
          if (po.donnees_extraites) {
            const ext = typeof po.donnees_extraites === 'string'
              ? JSON.parse(po.donnees_extraites) : po.donnees_extraites
            setRawExtracted(ext)
            buildFormFromExtracted(ext)
          }
        } catch (e) { toast.error('Erreur chargement') }
        finally { setLoading(false) }
      })()
    }
  }, [id, isNew])

  // Load reference data when tenant changes
  useEffect(() => {
    if (!tenantId) return
    const loadRef = async () => {
      try {
        const [vRes, cRes, pRes] = await Promise.all([
          fetch(`${API}/api/vehicules?tenant_id=${tenantId}&limit=500`),
          fetch(`${API}/api/chauffeurs?tenant_id=${tenantId}&limit=500`),
          fetch(`${API}/api/polices?tenant_id=${tenantId}&limit=100`),
        ])
        const [vD, cD, pD] = await Promise.all([vRes.json(), cRes.json(), pRes.json()])
        setVehicules(vD.vehicules || vD.data || [])
        setChauffeurs(cD.chauffeurs || cD.data || [])
        setPolices(pD.polices || pD.data || [])
      } catch (e) { console.error(e) }
    }
    loadRef()
  }, [tenantId])

  // Resolve selected vehicle when vehicules or form.vehicule_id changes
  useEffect(() => {
    if (form.vehicule_id && vehicules.length && !selectedVehicule) {
      const matched = vehicules.find(v => v.id === form.vehicule_id)
      if (matched) {
        setSelectedVehicule(matched)
        setVehiculeSearch(matched.immatriculation)
      }
    }
  }, [vehicules, form.vehicule_id, selectedVehicule])

  const buildFormFromExtracted = (ext) => {
    const val = (field) => {
      const f = ext[field]
      if (!f) return ''
      if (typeof f === 'object' && 'valeur' in f) return f.valeur ?? ''
      return f ?? ''
    }
    setForm({
      date_fait: val('date_fait'),
      date_declaration_client: val('date_declaration_client'),
      type_sinistre: val('type_sinistre'),
      lieu_sinistre: val('lieu_sinistre'),
      circonstances: val('circonstances'),
      dommages: val('dommages'),
      responsabilite: val('responsabilite'),
      vehicule_id: val('vehicule_id'),
      chauffeur_id: val('chauffeur_id'),
      police_id: val('police_id'),
      info_tiers: val('info_tiers'),
      nom_dossier_client: val('nom_dossier_client'),
      est_amiable: val('est_amiable') === true || val('est_amiable') === 'true',
      cas_bareme_irsa: val('cas_bareme_irsa'),
      note_interne: val('note_interne'),
      numero_affaire_compagnie: val('numero_affaire_compagnie'),
      numero_dossier_compagnie: val('numero_dossier_compagnie'),
      info_regleur_compagnie: val('info_regleur_compagnie'),
      info_expertise: val('info_expertise'),
      montant_dommages_estime: val('montant_dommages_estime'),
      montant_franchise_appliquee: val('montant_franchise_appliquee'),
      conducteur_detecte: val('conducteur_detecte'),
    })
    // Pre-fill vehicle search if AI matched a plate
    const plate = val('immatriculation_detectee')
    if (plate) setVehiculeSearch(plate)
    // If AI matched a vehicule_id, find it in vehicules to show
    const vid = val('vehicule_id')
    if (vid && vehicules.length) {
      const matched = vehicules.find(v => v.id === vid)
      if (matched) {
        setSelectedVehicule(matched)
        setVehiculeSearch(matched.immatriculation)
      }
    }
  }

  const updateForm = (field, value) => setForm(prev => ({ ...prev, [field]: value }))

  // Vehicle search with debounce
  const searchTimeoutRef = useRef(null)
  const handleVehiculeSearch = (query) => {
    setVehiculeSearch(query)
    if (!query || query.length < 2) {
      setVehiculeResults([])
      setShowVehiculeDropdown(false)
      return
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current)
    searchTimeoutRef.current = setTimeout(async () => {
      setVehiculeSearching(true)
      try {
        const r = await fetch(`${API}/api/vehicules?tenant_id=${tenantId}&search=${encodeURIComponent(query)}&limit=10`)
        const d = await r.json()
        setVehiculeResults(d.vehicules || [])
        setShowVehiculeDropdown(true)
      } catch (e) { console.error(e) }
      finally { setVehiculeSearching(false) }
    }, 300)
  }

  const selectVehicule = (v) => {
    setSelectedVehicule(v)
    setVehiculeSearch(v.immatriculation)
    updateForm('vehicule_id', v.id)
    setShowVehiculeDropdown(false)
  }

  const clearVehicule = () => {
    setSelectedVehicule(null)
    setVehiculeSearch('')
    updateForm('vehicule_id', '')
    setVehiculeResults([])
  }

  // Close vehicle dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (vehiculeDropdownRef.current && !vehiculeDropdownRef.current.contains(e.target)) {
        setShowVehiculeDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  // Create pre-ouverture
  const handleCreate = async () => {
    if (!tenantId || !contenuMail.trim()) {
      toast.error('Client et contenu du mail requis')
      return
    }
    setSaving(true)
    try {
      const r = await fetch(`${API}/api/pre-ouvertures`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          contenu_mail: contenuMail,
          notes_gestionnaire: notesGestionnaire || null,
        }),
      })
      const d = await r.json()
      if (!d.success) throw new Error('Erreur création')
      toast.success('Pré-ouverture créée')
      navigate(`/c2/pre-ouvertures/${d.pre_ouverture.id}`, { replace: true })
    } catch (e) { toast.error(e.message) }
    finally { setSaving(false) }
  }

  // Upload PJ with progress tracking
  const handleUploadPJ = async (files) => {
    for (const file of files) {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('tenant_id', tenantId)
      fd.append('type_document', file.type.startsWith('image/') ? 'photo' : 'autre')

      setUploadProgress(prev => ({ ...prev, [file.name]: { progress: 0, status: 'uploading' } }))

      try {
        const result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest()
          xhr.open('POST', `${API}/api/pre-ouvertures/${id}/pieces-jointes`)

          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) {
              const pct = Math.round((e.loaded / e.total) * 100)
              setUploadProgress(prev => ({ ...prev, [file.name]: { progress: pct, status: 'uploading' } }))
            }
          }

          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve(JSON.parse(xhr.responseText))
            } else {
              reject(new Error('Upload failed'))
            }
          }
          xhr.onerror = () => reject(new Error('Network error'))
          xhr.send(fd)
        })

        if (result.success) {
          setPiecesJointes(prev => [...prev, result.piece_jointe])
          setUploadProgress(prev => ({ ...prev, [file.name]: { progress: 100, status: 'done' } }))
          toast.success(`${file.name} ajouté`)
        }
      } catch (e) {
        setUploadProgress(prev => ({ ...prev, [file.name]: { progress: 0, status: 'error' } }))
        toast.error(`Erreur upload ${file.name}`)
      }
    }
    // Clear progress after 3s
    setTimeout(() => setUploadProgress({}), 3000)
  }

  // Launch AI analysis
  const handleAnalyse = async () => {
    setAnalysing(true)
    try {
      await fetch(`${API}/api/pre-ouvertures/${id}/analyser`, { method: 'POST' })
      // Poll
      let attempts = 0
      while (attempts < 60) {
        await new Promise(r => setTimeout(r, 2000))
        const sr = await fetch(`${API}/api/pre-ouvertures/${id}/analyse-status`)
        const sd = await sr.json()
        if (sd.status === 'done') {
          setStatut('analysee')
          setRawExtracted(sd.donnees_extraites || {})
          buildFormFromExtracted(sd.donnees_extraites || {})
          toast.success('Analyse IA terminée')
          break
        }
        if (sd.status === 'error' || sd.status === 'brouillon') {
          throw new Error('Erreur analyse IA')
        }
        attempts++
      }
    } catch (e) { toast.error(e.message) }
    finally { setAnalysing(false) }
  }

  // Validate → create sinistre
  const handleValidate = async () => {
    setValidating(true)
    try {
      const payload = { ...form }
      // Convert booleans
      payload.est_amiable = !!payload.est_amiable
      // Convert empty strings to null
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null
      })
      const r = await fetch(`${API}/api/pre-ouvertures/${id}/valider`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const d = await r.json()
      if (!d.success) throw new Error(d.detail || 'Erreur validation')
      toast.success(`Sinistre ${d.sinistre.numero_sinistre} créé`)
      navigate(`/c2/sinistres/${d.sinistre.id}`)
    } catch (e) { toast.error(e.message) }
    finally { setValidating(false) }
  }

  // Confidence indicator for a field
  const ConfBadge = ({ field }) => {
    const f = rawExtracted[field]
    if (!f || typeof f !== 'object' || !('confiance' in f)) return null
    const c = getConfidence(f.confiance)
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${c.color}`}>
        {Math.round(f.confiance * 100)}%
      </span>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  if (statut === 'validee' && sinistreId) {
    return (
      <div className="max-w-xl mx-auto mt-20 text-center space-y-4">
        <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto" />
        <h2 className="text-xl font-bold text-slate-800">Sinistre créé</h2>
        <p className="text-sm text-slate-500">Cette pré-ouverture a été validée et le sinistre a été créé.</p>
        <button
          onClick={() => navigate(`/c2/sinistres/${sinistreId}`)}
          className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700"
          data-testid="goto-sinistre"
        >
          Voir le sinistre
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-5" data-testid="pre-ouverture-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/c2/pre-ouvertures')}
            className="p-2 hover:bg-slate-100 rounded-lg"
          >
            <ArrowLeft size={18} className="text-slate-500" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-slate-800">
              {isNew ? 'Nouvelle pré-ouverture' : 'Pré-ouverture de sinistre'}
            </h1>
            {!isNew && (
              <p className="text-xs text-slate-500">ID: {id?.substring(0, 8)}... / Statut: {statut}</p>
            )}
          </div>
        </div>
        {!isNew && (
          <button
            onClick={async () => {
              if (!window.confirm('Supprimer cette pre-ouverture ? Cette action est irreversible.')) return
              try {
                const r = await fetch(`${API}/api/pre-ouvertures/${id}`, { method: 'DELETE' })
                if (r.ok) {
                  toast.success('Pre-ouverture supprimee')
                  navigate('/c2/pre-ouvertures')
                } else {
                  const d = await r.json()
                  toast.error(d.detail || 'Erreur suppression')
                }
              } catch { toast.error('Erreur reseau') }
            }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
            data-testid="po-delete-btn"
          >
            <Trash2 size={14} />
            Supprimer
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* LEFT: Input gestionnaire */}
        <div className="space-y-4">
          {/* Client selector */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <label className="block text-xs font-semibold text-slate-600 mb-2">Client *</label>
            <select
              value={tenantId}
              onChange={e => setTenantId(e.target.value)}
              disabled={!isNew}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50"
              data-testid="po-tenant-select"
            >
              <option value="">Sélectionner un client</option>
              {(tenants || []).map(t => (
                <option key={t.id} value={t.id}>{t.nom}</option>
              ))}
            </select>
          </div>

          {/* Mail content */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <label className="block text-xs font-semibold text-slate-600 mb-2">
              Contenu du mail client *
            </label>
            <textarea
              value={contenuMail}
              onChange={e => setContenuMail(e.target.value)}
              rows={10}
              placeholder="Collez ici le contenu du mail reçu du client..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y"
              disabled={statut === 'validee'}
              data-testid="po-mail-textarea"
            />
          </div>

          {/* Pieces jointes */}
          {!isNew && (
            <div className="bg-white border border-slate-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold text-slate-600">
                  Pièces jointes ({piecesJointes.length})
                </label>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  data-testid="upload-pj-btn"
                >
                  <Upload size={13} /> Ajouter
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={e => handleUploadPJ(Array.from(e.target.files))}
                  accept="image/*,.pdf,.doc,.docx"
                />
              </div>

              {/* Upload progress indicators */}
              {Object.entries(uploadProgress).length > 0 && (
                <div className="space-y-2 mb-3" data-testid="upload-progress-area">
                  {Object.entries(uploadProgress).map(([name, info]) => (
                    <div key={name} className="bg-slate-50 rounded-lg p-2.5">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-slate-600 truncate flex-1">{name}</span>
                        <span className={`text-[10px] font-medium ${
                          info.status === 'done' ? 'text-emerald-600' :
                          info.status === 'error' ? 'text-red-500' :
                          'text-indigo-600'
                        }`}>
                          {info.status === 'done' ? 'Terminé' :
                           info.status === 'error' ? 'Erreur' :
                           `${info.progress}%`}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-1.5">
                        <div
                          className={`h-1.5 rounded-full transition-all duration-300 ${
                            info.status === 'done' ? 'bg-emerald-500' :
                            info.status === 'error' ? 'bg-red-400' :
                            'bg-indigo-500'
                          }`}
                          style={{ width: `${info.progress}%` }}
                          data-testid={`upload-bar-${name}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {piecesJointes.length === 0 && Object.keys(uploadProgress).length === 0 ? (
                <div
                  className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-indigo-300 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Photos, devis, constat, documents</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {piecesJointes.map(pj => (
                    <div key={pj.id} className="flex items-center gap-2 bg-slate-50 rounded-lg p-2">
                      {pj.type_fichier?.startsWith('image/') ? (
                        <Image size={14} className="text-blue-500" />
                      ) : (
                        <FileText size={14} className="text-slate-500" />
                      )}
                      <span className="text-xs text-slate-700 flex-1 truncate">{pj.nom_fichier}</span>
                      <span className="text-[10px] text-slate-400">
                        {pj.taille ? `${(pj.taille / 1024).toFixed(0)} Ko` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notes gestionnaire */}
          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <label className="block text-xs font-semibold text-slate-600 mb-2">Notes gestionnaire</label>
            <textarea
              value={notesGestionnaire}
              onChange={e => setNotesGestionnaire(e.target.value)}
              rows={3}
              placeholder="Notes internes..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y"
              data-testid="po-notes-textarea"
            />
          </div>

          {/* Actions left column */}
          <div className="flex gap-3">
            {isNew ? (
              <button
                onClick={handleCreate}
                disabled={saving || !tenantId || !contenuMail.trim()}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                data-testid="po-create-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
                Créer la pré-ouverture
              </button>
            ) : statut === 'brouillon' || statut === 'analysee' ? (
              <button
                onClick={handleAnalyse}
                disabled={analysing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-xl hover:bg-indigo-700 disabled:opacity-50"
                data-testid="po-analyse-btn"
              >
                {analysing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyse IA en cours...
                  </>
                ) : (
                  <>
                    <Send size={16} />
                    {statut === 'analysee' ? 'Relancer l\'analyse IA' : 'Analyser avec l\'IA'}
                  </>
                )}
              </button>
            ) : null}
          </div>
        </div>

        {/* RIGHT: AI-proposed sinistre form */}
        <div className="space-y-4">
          {statut === 'brouillon' && !analysing ? (
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
              <Send className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">En attente d'analyse</p>
              <p className="text-xs text-slate-400 mt-1">
                Collez le mail, ajoutez les PJ, puis lancez l'analyse IA.
              </p>
            </div>
          ) : analysing ? (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-8 text-center">
              <Loader2 className="w-10 h-10 text-indigo-400 mx-auto mb-3 animate-spin" />
              <p className="text-sm font-medium text-indigo-700">Analyse IA en cours...</p>
              <p className="text-xs text-indigo-500 mt-1">
                L'IA analyse le mail et les pièces jointes pour pré-remplir la fiche sinistre.
              </p>
            </div>
          ) : (
            <>
              {/* Confidence banner */}
              {rawExtracted.confiance_globale != null && (
                <div className={`border rounded-xl p-3 flex items-center gap-3 ${
                  rawExtracted.confiance_globale >= 0.8 ? 'bg-emerald-50 border-emerald-200' :
                  rawExtracted.confiance_globale >= 0.5 ? 'bg-amber-50 border-amber-200' :
                  'bg-red-50 border-red-200'
                }`} data-testid="confidence-banner">
                  <span className="text-2xl font-bold">
                    {Math.round(rawExtracted.confiance_globale * 100)}%
                  </span>
                  <div>
                    <p className="text-xs font-medium">Confiance globale IA</p>
                    <p className="text-[10px] opacity-70">Vérifiez les champs en jaune/rouge</p>
                  </div>
                </div>
              )}

              {/* Identification */}
              <Section title="Identification">
                <FormField label="Nom du dossier" field="nom_dossier_client" badge={<ConfBadge field="nom_dossier_client" />}>
                  <input type="text" value={form.nom_dossier_client || ''} onChange={e => updateForm('nom_dossier_client', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-nom-dossier" />
                </FormField>
                <FormField label="Police (contrat)" field="police_id" badge={<ConfBadge field="police_id" />}>
                  <select value={form.police_id || ''} onChange={e => updateForm('police_id', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-police">
                    <option value="">Sélectionner</option>
                    {polices.map(p => <option key={p.id} value={p.id}>{p.numero_police} — {p.libelle || p.type_police}</option>)}
                  </select>
                </FormField>
                <FormField label="Véhicule (recherche par immatriculation)" field="vehicule_id" badge={<ConfBadge field="vehicule_id" />}>
                  <div className="relative" ref={vehiculeDropdownRef}>
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          ref={vehiculeSearchRef}
                          type="text"
                          value={vehiculeSearch}
                          onChange={e => handleVehiculeSearch(e.target.value)}
                          onFocus={() => { if (vehiculeResults.length) setShowVehiculeDropdown(true) }}
                          placeholder="Tapez une immatriculation..."
                          className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm"
                          data-testid="form-vehicule-search"
                        />
                        {vehiculeSearching && (
                          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
                        )}
                      </div>
                      {selectedVehicule && (
                        <button onClick={clearVehicule} className="p-1.5 hover:bg-slate-100 rounded" data-testid="clear-vehicule-btn">
                          <X size={14} className="text-slate-400" />
                        </button>
                      )}
                    </div>
                    {selectedVehicule && (
                      <div className="mt-1.5 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-1.5" data-testid="selected-vehicule">
                        <Car size={13} className="text-indigo-500" />
                        <span className="text-xs font-medium text-indigo-700">
                          {selectedVehicule.immatriculation} — {selectedVehicule.marque} {selectedVehicule.modele}
                        </span>
                      </div>
                    )}
                    {showVehiculeDropdown && vehiculeResults.length > 0 && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="vehicule-dropdown">
                        {vehiculeResults.map(v => (
                          <button
                            key={v.id}
                            onClick={() => selectVehicule(v)}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center gap-2 transition-colors"
                            data-testid={`vehicule-option-${v.id}`}
                          >
                            <Car size={13} className="text-slate-400 shrink-0" />
                            <span className="font-medium text-slate-700">{v.immatriculation}</span>
                            <span className="text-slate-400">— {v.marque} {v.modele}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    {showVehiculeDropdown && vehiculeSearch.length >= 2 && vehiculeResults.length === 0 && !vehiculeSearching && (
                      <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-3 text-center">
                        <p className="text-xs text-slate-400">Aucun véhicule trouvé pour "{vehiculeSearch}"</p>
                      </div>
                    )}
                  </div>
                </FormField>
                <FormField label="Conducteur" field="chauffeur_id" badge={<ConfBadge field="conducteur_detecte" />}>
                  {form.conducteur_detecte && (
                    <div className="mb-2 flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5" data-testid="conducteur-detecte-banner">
                      <AlertTriangle size={12} className="text-amber-500 shrink-0" />
                      <span className="text-xs text-amber-700">
                        Conducteur détecté par l'IA : <strong>{form.conducteur_detecte}</strong>
                      </span>
                    </div>
                  )}
                  <input
                    type="text"
                    value={form.conducteur_detecte || ''}
                    onChange={e => updateForm('conducteur_detecte', e.target.value)}
                    placeholder="Nom du conducteur (éditable)"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm mb-2"
                    data-testid="form-conducteur-nom"
                  />
                  <select value={form.chauffeur_id || ''} onChange={e => updateForm('chauffeur_id', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-chauffeur">
                    <option value="">Associer à un chauffeur existant (optionnel)</option>
                    {chauffeurs.map(c => <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>)}
                  </select>
                </FormField>
              </Section>

              {/* Sinistre */}
              <Section title="Informations sinistre">
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Date sinistre" field="date_fait" badge={<ConfBadge field="date_fait" />}>
                    <input type="date" value={form.date_fait || ''} onChange={e => updateForm('date_fait', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-date-fait" />
                  </FormField>
                  <FormField label="Date déclaration client" field="date_declaration_client" badge={<ConfBadge field="date_declaration_client" />}>
                    <input type="date" value={form.date_declaration_client || ''} onChange={e => updateForm('date_declaration_client', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </FormField>
                </div>
                <FormField label="Type sinistre" field="type_sinistre" badge={<ConfBadge field="type_sinistre" />}>
                  <select value={form.type_sinistre || ''} onChange={e => updateForm('type_sinistre', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-type-sinistre">
                    <option value="">Sélectionner</option>
                    {TYPE_SINISTRE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Lieu" field="lieu_sinistre" badge={<ConfBadge field="lieu_sinistre" />}>
                  <input type="text" value={form.lieu_sinistre || ''} onChange={e => updateForm('lieu_sinistre', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-lieu" />
                </FormField>
                <FormField label="Circonstances" field="circonstances" badge={<ConfBadge field="circonstances" />}>
                  <textarea value={form.circonstances || ''} onChange={e => updateForm('circonstances', e.target.value)}
                    rows={3} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y" data-testid="form-circonstances" />
                </FormField>
                <FormField label="Dommages" field="dommages" badge={<ConfBadge field="dommages" />}>
                  <textarea value={form.dommages || ''} onChange={e => updateForm('dommages', e.target.value)}
                    rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y" data-testid="form-dommages" />
                </FormField>
              </Section>

              {/* Gestion assurance */}
              <Section title="Gestion assurance">
                <FormField label="Responsabilité" field="responsabilite" badge={<ConfBadge field="responsabilite" />}>
                  <select value={form.responsabilite || ''} onChange={e => updateForm('responsabilite', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="form-responsabilite">
                    <option value="">Sélectionner</option>
                    {RESPONSABILITE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </FormField>
                <FormField label="Cas barème IRSA" field="cas_bareme_irsa" badge={<ConfBadge field="cas_bareme_irsa" />}>
                  <input type="text" value={form.cas_bareme_irsa || ''} onChange={e => updateForm('cas_bareme_irsa', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder="Ex: cas n°3" />
                </FormField>
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="N° affaire compagnie">
                    <input type="text" value={form.numero_affaire_compagnie || ''} onChange={e => updateForm('numero_affaire_compagnie', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </FormField>
                  <FormField label="N° dossier compagnie">
                    <input type="text" value={form.numero_dossier_compagnie || ''} onChange={e => updateForm('numero_dossier_compagnie', e.target.value)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </FormField>
                </div>
              </Section>

              {/* Tiers & notes */}
              <Section title="Informations tierces & notes">
                <FormField label="Informations tiers" field="info_tiers" badge={<ConfBadge field="info_tiers" />}>
                  <textarea value={form.info_tiers || ''} onChange={e => updateForm('info_tiers', e.target.value)}
                    rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y" data-testid="form-info-tiers" />
                </FormField>
                <FormField label="Note interne">
                  <textarea value={form.note_interne || ''} onChange={e => updateForm('note_interne', e.target.value)}
                    rows={2} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-y" />
                </FormField>
                <div className="flex items-center gap-2 mt-2">
                  <input type="checkbox" checked={!!form.est_amiable} onChange={e => updateForm('est_amiable', e.target.checked)}
                    className="rounded border-slate-300" id="amiable-check" data-testid="form-amiable" />
                  <label htmlFor="amiable-check" className="text-xs text-slate-600">Transaction amiable</label>
                </div>
              </Section>

              {/* Validate button */}
              {(statut === 'analysee' || statut === 'brouillon') && (
                <div className="flex gap-3">
                  <button
                    onClick={handleValidate}
                    disabled={validating}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 text-white text-sm font-semibold rounded-xl hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                    data-testid="po-validate-btn"
                  >
                    {validating ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle size={16} />}
                    Valider et créer le sinistre
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ──

function Section({ title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <div className="px-4 py-2.5 bg-slate-50 border-b border-slate-100">
        <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="p-4 space-y-3">{children}</div>
    </div>
  )
}

function FormField({ label, field, badge, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <label className="text-xs font-medium text-slate-600">{label}</label>
        {badge}
      </div>
      {children}
    </div>
  )
}
